import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, FileText, Send, MapPin, Lock, Globe2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCity } from '../contexts/CityContext';
import { useAccount } from '../contexts/AccountContext';
import { DistrictMap } from './requetes/DistrictMap';
import { districtsFor, districtPoints, sectorShare, type CityDistrict } from '../utils/districts';
import { sectorsFor } from '../data/communications';
import { requestPinsFor, type SlaState } from '../utils/requestPins';
import { RequestDetailPanel } from './requetes/RequestPinPopup';
import { AtnaDataAssistant } from './requetes/AtnaDataAssistant';
import { DEFAULT_RANGE, rangeDays, type DateRange } from './requetes/DateRangePicker';
import { TerritorialBriefingPanel } from './requetes/TerritorialBriefingPanel';
import { buildBriefing } from '../utils/territorialBriefing';
import { CouncilReportDialog } from './requetes/CouncilReportDialog';
import { DashboardKpis, type KpiTarget } from './requetes/DashboardKpis';
import { fmtDec, fmtNum, statsFor, type AttentionTarget } from '../utils/requestDashboard';
import { WATCH_DISTRICTS, WATCH_SECTOR_IDS } from '../data/requestDashboard';
import { ATNA_DATA_SUGGESTIONS } from '../utils/atnaDataAnswers';
import type { DatePreset } from './requetes/DateRangePicker';
import type { Localized } from '../types/notice';
const PERIOD_LABELS: Record<DatePreset, Localized> = {
  '24h': {
    fr: 'Dernières 24 heures',
    en: 'Last 24 hours'
  },
  '7d': {
    fr: '7 derniers jours',
    en: 'Last 7 days'
  },
  '30d': {
    fr: '30 derniers jours',
    en: 'Last 30 days'
  },
  '90d': {
    fr: '3 derniers mois',
    en: 'Last 3 months'
  },
  ytd: {
    fr: 'Depuis le 1er janvier 2026',
    en: 'Since January 1, 2026'
  },
  custom: {
    fr: 'Période personnalisée',
    en: 'Custom period'
  }
};
// --- DATA ---
type Status = 'Sous la cible' | 'À surveiller' | 'Conforme';
interface ServiceData {
  id: string;
  service: string;
  serviceEn: string;
  category: string;
  categoryEn: string;
  target: string;
  targetEn: string;
  performance: number;
  status: Status;
}
const initialData: ServiceData[] = [{
  id: '1',
  service: 'Éclairage de parc',
  serviceEn: 'Park lighting',
  category: 'Éclairage',
  categoryEn: 'Lighting',
  target: '21 jours',
  targetEn: '21 days',
  performance: 89,
  status: 'Conforme'
}, {
  id: '2',
  service: 'Réparation lampadaire',
  serviceEn: 'Streetlight repair',
  category: 'Éclairage',
  categoryEn: 'Lighting',
  target: '14 jours',
  targetEn: '14 days',
  performance: 92,
  status: 'Conforme'
}, {
  id: '3',
  service: 'Signalisation routière',
  serviceEn: 'Road signage',
  category: 'Voirie',
  categoryEn: 'Roads',
  target: '180 jours',
  targetEn: '180 days',
  performance: 76,
  status: 'À surveiller'
}, {
  id: '4',
  service: 'Nid-de-poule',
  serviceEn: 'Pothole',
  category: 'Voirie',
  categoryEn: 'Roads',
  target: '7 jours',
  targetEn: '7 days',
  performance: 71,
  status: 'À surveiller'
}, {
  id: '5',
  service: 'Réparation d’un bac brisé',
  serviceEn: 'Broken bin repair',
  category: 'Matières résiduelles',
  categoryEn: 'Waste management',
  target: '14 jours',
  targetEn: '14 days',
  performance: 96,
  status: 'Conforme'
}];
export interface ImpactedService {
  name: string;
  nameEn: string;
  requests: number;
  sla: number;
}
export interface District {
  id: string;
  name: string;
  nameEn: string;
  label: string;
  sla: number;
  totalRequests: number;
  reachable: number;
  activeCards: number;
  openRate: number;
  impactedServices: ImpactedService[];
  points: string;
  labelX: number;
  labelY: number;
}
const districts: District[] = [{
  id: 'A',
  name: 'Secteur A',
  nameEn: 'District A',
  label: 'A',
  sla: 86,
  totalRequests: 412,
  reachable: 83,
  activeCards: 4182,
  openRate: 71,
  impactedServices: [{
    name: 'Éclairage',
    nameEn: 'Lighting',
    requests: 163,
    sla: 84
  }, {
    name: 'Signalisation routière',
    nameEn: 'Road signage',
    requests: 124,
    sla: 88
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 88,
    sla: 91
  }],
  points: '20,20 200,20 200,160 20,160',
  labelX: 110,
  labelY: 95
}, {
  id: 'B',
  name: 'Secteur B',
  nameEn: 'District B',
  label: 'B',
  sla: 90,
  totalRequests: 287,
  reachable: 76,
  activeCards: 3214,
  openRate: 64,
  impactedServices: [{
    name: 'Réparation lampadaire',
    nameEn: 'Streetlight repair',
    requests: 102,
    sla: 87
  }, {
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 71,
    sla: 92
  }, {
    name: 'Graffitis',
    nameEn: 'Graffiti',
    requests: 48,
    sla: 90
  }],
  points: '200,20 400,20 400,160 200,160',
  labelX: 300,
  labelY: 95
}, {
  id: 'C',
  name: 'Secteur C',
  nameEn: 'District C',
  label: 'C',
  sla: 84,
  totalRequests: 196,
  reachable: 88,
  activeCards: 2876,
  openRate: 79,
  impactedServices: [{
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 58,
    sla: 81
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 47,
    sla: 88
  }, {
    name: 'Réparation d’un bac brisé',
    nameEn: 'Broken bin repair',
    requests: 33,
    sla: 86
  }],
  points: '400,20 580,20 580,160 400,160',
  labelX: 490,
  labelY: 95
}, {
  id: 'L',
  name: 'Secteur L',
  nameEn: 'District L',
  label: 'L',
  sla: 88,
  totalRequests: 318,
  reachable: 81,
  activeCards: 3654,
  openRate: 68,
  impactedServices: [{
    name: 'Signalisation routière',
    nameEn: 'Road signage',
    requests: 118,
    sla: 86
  }, {
    name: 'Éclairage de parc',
    nameEn: 'Park lighting',
    requests: 82,
    sla: 89
  }, {
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 64,
    sla: 93
  }],
  points: '20,160 200,160 200,300 20,300',
  labelX: 110,
  labelY: 235
}, {
  id: 'M',
  name: 'Secteur M',
  nameEn: 'District M',
  label: 'M',
  sla: 84,
  totalRequests: 351,
  reachable: 79,
  activeCards: 3982,
  openRate: 66,
  impactedServices: [{
    name: 'Éclairage',
    nameEn: 'Lighting',
    requests: 134,
    sla: 82
  }, {
    name: 'Signalisation routière',
    nameEn: 'Road signage',
    requests: 96,
    sla: 87
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 67,
    sla: 82
  }],
  points: '200,160 400,160 400,300 200,300',
  labelX: 300,
  labelY: 235
}, {
  id: 'O',
  name: 'Secteur O',
  nameEn: 'District O',
  label: 'O',
  sla: 89,
  totalRequests: 142,
  reachable: 91,
  activeCards: 2412,
  openRate: 82,
  impactedServices: [{
    name: 'Réparation d’un bac brisé',
    nameEn: 'Broken bin repair',
    requests: 41,
    sla: 90
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 36,
    sla: 92
  }, {
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 28,
    sla: 85
  }],
  points: '400,160 580,160 580,300 400,300',
  labelX: 490,
  labelY: 235
}, {
  id: 'R',
  name: 'Secteur R',
  nameEn: 'District R',
  label: 'R',
  sla: 91,
  totalRequests: 224,
  reachable: 84,
  activeCards: 2987,
  openRate: 73,
  impactedServices: [{
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 78,
    sla: 89
  }, {
    name: 'Graffitis',
    nameEn: 'Graffiti',
    requests: 52,
    sla: 92
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 41,
    sla: 86
  }],
  points: '20,300 300,300 300,400 20,400',
  labelX: 160,
  labelY: 355
}, {
  id: 'S',
  name: 'Secteur S',
  nameEn: 'District S',
  label: 'S',
  sla: 82,
  totalRequests: 187,
  reachable: 86,
  activeCards: 2614,
  openRate: 75,
  impactedServices: [{
    name: 'Signalisation routière',
    nameEn: 'Road signage',
    requests: 62,
    sla: 90
  }, {
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 49,
    sla: 84
  }, {
    name: 'Réparation d’un bac brisé',
    nameEn: 'Broken bin repair',
    requests: 31,
    sla: 88
  }],
  points: '300,300 580,300 580,400 300,400',
  labelX: 440,
  labelY: 355
}];
function getStatusForSla(sla: number): 'critical' | 'warning' | 'on-track' {
  if (sla < 60) return 'critical';
  if (sla < 80) return 'warning';
  return 'on-track';
}
// --- COMPONENTS ---
function formatScaled(value: number, factor: number, lang: string) {
  return Math.round(value * factor).toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA');
}

/** Tells the user which part of the territory their account can see. */
function ScopeBanner({
  name,
  role,
  district
}: {
  name: string;
  role: string;
  district: CityDistrict | null;
}) {
  const {
    t,
    lang
  } = useLanguage();
  if (!district) {
    return <div className="mt-2 inline-flex self-start items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600 font-body" data-id="element-5693" data-mp="2ovykmtol3qt7">
        <Globe2 className="w-3.5 h-3.5 text-slate-400" data-id="element-5694" data-mp="uktbj9uqam6h" />
        <span data-id="element-5695" data-mp="1fqrdgam1az2z">
          <span className="font-semibold text-[#0F172A]" data-id="element-5696" data-mp="1fr9ctg3zttf4">
            {t('Vue : toute la ville', 'View: whole city')}
          </span>{' '}
          · {name}, {role}
        </span>
      </div>;
  }
  const letters = district.sectors.map(s => s.label[lang].replace(/^(Secteurs?|Sectors?) /, ''));
  const list = letters.length > 1 ? `${letters.slice(0, -1).join(', ')} ${t('et', 'and')} ${letters[letters.length - 1]}` : letters[0];
  return <div role="status" className="mt-2 flex items-start gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 font-body" data-id="element-5697" data-mp="1yniyopl4zo9o">
      <div className="w-8 h-8 rounded-lg bg-[#4F46E5] text-white flex items-center justify-center shrink-0" data-id="element-5698" data-mp="28ggciif6op1y">
        <Lock className="w-4 h-4" data-id="element-5699" data-mp="2dd6rt3j5zgmb" />
      </div>
      <div className="flex flex-col gap-0.5" data-id="element-5700" data-mp="2dgckstuwz2nb">
        <div className="text-sm font-bold text-[#0F172A]" data-id="element-5701" data-mp="2iemcuvlcdn4p">
          {t(`Vue limitée au ${district.label.fr}`, `View limited to ${district.label.en}`)}
        </div>
        <p className="text-sm text-slate-600 m-0" data-id="element-5702" data-mp="arkzs41qk5xg">
          {t(`${name}, ${role.charAt(0).toLowerCase()}${role.slice(1)} : vous voyez uniquement les requêtes de votre district (secteur${letters.length > 1 ? 's' : ''} ${list}). Les autres districts sont masqués.`, `${name}, ${role}: you only see requests from your district (sector${letters.length > 1 ? 's' : ''} ${list}). Other districts are hidden.`)}
        </p>
      </div>
    </div>;
}
function KPIGrid({
  factor = 1,
  seed = 0
}: {
  factor?: number;
  seed?: number;
}) {
  const {
    t,
    lang
  } = useLanguage();
  const scoped = factor < 1;
  const onTime = scoped ? 93 + seed % 5 - 2 : 94;
  const below = 0;
  const watch = scoped ? seed % 2 : 1;
  const avgDays = scoped ? Math.round((3.8 + (seed % 7 - 3) / 10) * 10) / 10 : 3.8;
  const satisfaction = scoped ? Math.round((4.6 + (seed % 3 - 1) / 10) * 10) / 10 : 4.6;
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 content-start" data-id="element-5703" data-mp="18fbh4ukhlr6u">
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5704" data-mp="32np3thaqcdl4">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5705" data-mp="36osii0h1k3kr">
          {t('Requêtes reçues', 'Requests received')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5706" data-mp="2swuicf5b5i0q">
          {formatScaled(60058, factor, lang)}
        </div>
        <div className="text-sm text-slate-500 font-body" data-id="element-5707" data-mp="2xumana9v765g">
          {t('+11 % vs mois précédent', '+11% vs. previous month')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5708" data-mp="2nzlwa3h1gtu7">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5709" data-mp="29zt9gj8muxri">
          {t('Résolues dans les délais', 'Resolved on time')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5710" data-mp="2ezpdgihbyp72">
          {onTime} %
        </div>
        <div className="text-sm text-emerald-600 font-medium font-body" data-id="element-5711" data-mp="d6ho0g806r7g">
          {t('+10 points', '+10 points')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5712" data-mp="nbhlep780uor">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5713" data-mp="icejhdmjf4l0">
          {t('Services sous la cible', 'Services below target')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5714" data-mp="w4ktsj30ehcj">{below}</div>
        <div className={`text-sm font-medium font-body ${below > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-id="element-5715" data-mp="r9x78prkfx0l">
          {below > 0 ? t('Action requise', 'Action required') : t('Aucune action', 'No action needed')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5716" data-mp="1f0kqg76xm92g">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5717" data-mp="1b1289j6hwc3b">
          {t('Services à surveiller', 'Services to watch')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5718" data-mp="3gusf0lk39xqv">{watch}</div>
        <div className="text-sm text-amber-600 font-medium font-body" data-id="element-5719" data-mp="3ct6xgmwm0vac">
          {t('Tendance à la baisse', 'Trending down')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5720" data-mp="3j6umbg6jxgc7">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5721" data-mp="3n8zeeahcq4wq">
          {t('Délai moyen de résolution', 'Average resolution time')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5722" data-mp="3s5y66mdjlzrr">
          {avgDays.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')} {t('j', 'd')}
        </div>
        <div className="text-sm text-emerald-600 font-medium font-body" data-id="element-5723" data-mp="2zk6c7wa4z0to">
          {t('−0,6 j vs mois précédent', '−0.6 d vs. previous month')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5724" data-mp="399pwn6dhz2qn">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5725" data-mp="2buu5r46716v2">
          {t('Satisfaction citoyenne', 'Citizen satisfaction')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5726" data-mp="2gr2fg21mg7uu">
          {satisfaction.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')}
          <span className="text-lg text-slate-400 font-semibold" data-id="element-5727" data-mp="24a9u5fjw48h0"> / 5</span>
        </div>
        <div className="text-sm text-slate-500 font-body" data-id="element-5728" data-mp="1zax5cp9gkrbs">
          {t('Sondage après fermeture · 38 % de réponse', 'Post-closure survey · 38% response')}
        </div>
      </div>
    </div>;
}
// --- PER-SECTOR DATA ---
/** Stable pseudo-random seed from a sector id, so figures don't change between renders. */
function hashOf(value: string) {
  let h = 0;
  for (let i = 0; i < value.length; i++) {
    h = h * 31 + value.charCodeAt(i) >>> 0;
  }
  return h;
}
function statusForPerformance(p: number): Status {
  if (p < 60) return 'Sous la cible';
  if (p < 80) return 'À surveiller';
  return 'Conforme';
}
/** Every service's SLA performance inside one sector. */
/**
 * Services behind schedule in the watch sectors (districts 3 and 9), so the
 * table matches the amber districts: potholes in B, streetlights in O and I.
 */
const WATCH_SECTOR_SERVICES: Record<string, Record<string, number>> = {
  'br-b': {
    '4': 64
  },
  'br-o': {
    '2': 70
  },
  'br-i': {
    '2': 72
  }
};
function sectorServices(sectorId: string): ServiceData[] {
  const h = hashOf(sectorId);
  return initialData.map((row, i) => {
    const offset = (h >>> i * 3) % 9 - 4;
    const forced = WATCH_SECTOR_SERVICES[sectorId]?.[row.id];
    const performance = forced ?? Math.max(18, Math.min(98, row.performance + offset));
    return {
      ...row,
      performance,
      status: statusForPerformance(performance)
    };
  });
}
function sectorRequests(sectorId: string) {
  // ~71 per sector on average, so 18 sectors ≈ the 1 284 requests of the period.
  return 42 + hashOf(sectorId) % 58;
}
/** bciti+ digital reach — real figures when the sector has data, otherwise stable estimates. */
function sectorReach(sectorId: string, letter: string) {
  const d = districts.find(x => x.label === letter);
  if (d) {
    return {
      reachable: d.reachable,
      activeCards: d.activeCards,
      openRate: d.openRate
    };
  }
  const h = hashOf(sectorId);
  return {
    reachable: 62 + h % 28,
    activeCards: 1800 + h % 3200,
    openRate: 55 + (h >>> 4) % 30
  };
}
/** Sector letter, e.g. "Secteur L" → "L". */
function letterOf(label: string) {
  return label.replace(/^Secteurs? /, '');
}
function PerformanceTable({
  rows
}: {
  rows: ServiceData[];
}) {
  const {
    t,
    lang
  } = useLanguage();
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ServiceData;
    direction: 'asc' | 'desc';
  }>({
    key: 'performance',
    direction: 'asc'
  });
  const handleSort = (key: keyof ServiceData) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({
      key,
      direction
    });
  };
  const data = [...rows].sort((a, b) => {
    const {
      key,
      direction
    } = sortConfig;
    if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
    if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
    return 0;
  });
  const getStatusColor = (status: Status) => {
    switch (status) {
      case 'Sous la cible':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'À surveiller':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Conforme':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
  };
  const statusLabel = (status: Status) => {
    switch (status) {
      case 'Sous la cible':
        return t('Sous la cible', 'Below target');
      case 'À surveiller':
        return t('À surveiller', 'To watch');
      case 'Conforme':
        return t('Conforme', 'On track');
    }
  };
  const getBarColor = (performance: number) => {
    if (performance < 60) return 'bg-red-500';
    if (performance < 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  };
  const SortIcon = ({
    columnKey
  }: {
    columnKey: keyof ServiceData;
  }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 text-slate-300 ml-1 inline-block" data-id="element-5729" data-mp="3vh24og105uo5" />;
    return sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-slate-600 ml-1 inline-block" data-id="element-5730" data-mp="3qoir3j2bezjc" /> : <ArrowDown className="w-3 h-3 text-slate-600 ml-1 inline-block" data-id="element-5731" data-mp="3ls0pgs2a7hb8" />;
  };
  return <div className="overflow-x-auto" data-id="element-5732" data-mp="3hq6rwsndutfa">
      <table className="w-full text-left text-sm whitespace-nowrap font-body" data-id="element-5733" data-mp="3hr8g7ytetcey">
        <thead className="bg-slate-50 border-y border-slate-200 text-slate-600 text-xs" data-id="element-5734" data-mp="i8z3azkey9bo">
          <tr data-id="element-5735" data-mp="dc8osgo9blsd">
            <th className="px-4 py-2.5 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('service')} data-id="element-5736" data-mp="dbp4itb4wlb6">
              {t('Service', 'Service')} <SortIcon columnKey="service" data-id="element-5737" data-mp="4a7sbm0ul3rr" />
            </th>
            <th className="px-4 py-2.5 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('target')} data-id="element-5738" data-mp="2u02ytf7tmc2j">
              {t('SLA', 'SLA')} <SortIcon columnKey="target" data-id="element-5739" data-mp="1pd9vdntnbev1" />
            </th>
            <th className="px-4 py-2.5 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('performance')} data-id="element-5740" data-mp="1kljfl2bxvyw8">
              {t('SLA respecté', 'SLA met')}{' '}
              <SortIcon columnKey="performance" data-id="element-5741" data-mp="1blwe6gj7mt36" />
            </th>
            <th className="px-4 py-2.5 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('status')} data-id="element-5742" data-mp="28b437yj1fkbu">
              {t('État', 'Status')} <SortIcon columnKey="status" data-id="element-5743" data-mp="1za5ux41kgqng" />
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100" data-id="element-5744" data-mp="1ubbda5fc584x">
          {data.map(row => <tr key={row.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-5745" data-mp="1udp4g36wa077">
              <td className="px-4 py-3" data-id="element-5746" data-mp="nwk47eg26gzt">
                <div className="font-medium text-[#0F172A]" data-id="element-5747" data-mp="ivmr3g9wod56">
                  {lang === 'fr' ? row.service : row.serviceEn}
                </div>
                <div className="text-xs text-slate-500" data-id="element-5748" data-mp="8u29ydg3loqv">
                  {lang === 'fr' ? row.category : row.categoryEn}
                </div>
              </td>
              <td className="px-4 py-3 text-slate-500" data-id="element-5749" data-mp="iq58zafnl79l">
                {lang === 'fr' ? row.target : row.targetEn}
              </td>
              <td className="px-4 py-3" data-id="element-5750" data-mp="niga2gm4qz3q">
                <div className="flex items-center gap-3" data-id="element-5751" data-mp="riz3ug7l2f8k">
                  <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-5752" data-mp="rih06yilcyqm">
                    <div className={`h-full rounded-full ${getBarColor(row.performance)}`} style={{
                  width: `${row.performance}%`
                }} data-id="element-5753" data-mp="wg8s3sj8kkut" />
                  </div>
                  <span className="text-slate-700 font-medium w-10" data-id="element-5754" data-mp="3391g443tmp6m">
                    {row.performance} %
                  </span>
                </div>
              </td>
              <td className="px-4 py-3" data-id="element-5755" data-mp="306omsc1ubtsq">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(row.status)}`} data-id="element-5756" data-mp="307g54l6ffv12">
                  {statusLabel(row.status)}
                </span>
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}
/** Scope id meaning "the whole city" in the district / sector drop-down. */
const ALL_SCOPE = 'all';
/** Average each service's performance over the chosen sectors. */
function aggregateServices(sectorIds: string[]): ServiceData[] {
  const perSector = sectorIds.map(sectorServices);
  return initialData.map((row, i) => {
    const performance = Math.round(perSector.reduce((sum, rows) => sum + rows[i].performance, 0) / perSector.length);
    return {
      ...row,
      performance,
      status: statusForPerformance(performance)
    };
  });
}
interface ScopeOption {
  id: string;
  label: Localized;
}
/** One slice of the analysis: a sector, or a sector ∩ district. */
interface ScopeUnit {
  key: string;
  letter: string;
  /** Share of the base sector's volume in this slice. */
  share: number;
  baseSectorId: string;
}
function DistrictDetailPanel({
  mode,
  showSecondary,
  primaryOptions,
  primaryId,
  onSelectPrimary,
  secondaryOptions,
  activeSecondaryIds,
  onToggleSecondary,
  onSelectAllSecondary,
  units,
  scopeLabel
}: {
  mode: 'district' | 'sector';
  showSecondary: boolean;
  primaryOptions: ScopeOption[];
  primaryId: string;
  onSelectPrimary: (id: string) => void;
  secondaryOptions: ScopeOption[];
  activeSecondaryIds: string[];
  onToggleSecondary: (id: string) => void;
  onSelectAllSecondary: () => void;
  units: ScopeUnit[];
  scopeLabel: Localized;
}) {
  const {
    t,
    lang
  } = useLanguage();
  const rows = aggregateServices(units.map(u => u.key));
  const reaches = units.map(u => sectorReach(u.key, u.letter));
  const reach = {
    reachable: Math.round(reaches.reduce((sum, r) => sum + r.reachable, 0) / reaches.length),
    activeCards: Math.round(reaches.reduce((sum, r, i) => sum + r.activeCards * units[i].share, 0)),
    openRate: Math.round(reaches.reduce((sum, r) => sum + r.openRate, 0) / reaches.length)
  };
  const totalRequests = Math.round(units.reduce((sum, u) => sum + sectorRequests(u.baseSectorId) * u.share, 0));
  const allSelected = activeSecondaryIds.length === secondaryOptions.length;
  const isDistrictMode = mode === 'district';
  const primaryTitle = primaryId === ALL_SCOPE ? t('Territoire', 'Area') : isDistrictMode && showSecondary ? t('District', 'District') : t('Secteur sélectionné', 'Selected sector');
  const secondaryTitle = t('Secteurs', 'Sectors');
  return <div className="border border-slate-200 rounded-xl shadow-sm bg-white flex flex-col h-full overflow-hidden font-body" data-id="element-5757" data-mp="3phyh4yxzsrzv">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3" data-id="element-5758" data-mp="1noigd986xwr6">
        <div className="flex flex-col gap-1 shrink-0" data-id="element-5759" data-mp="1v7roz08vhrf6">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide" data-id="element-5760" data-mp="1v6ymibgy5o2u">
            <MapPin className="w-3.5 h-3.5" data-id="element-5761" data-mp="2059zuw6gagpt" /> {primaryTitle}
          </div>
          <label className="sr-only" htmlFor="scope-select" data-id="element-5762" data-mp="24xb8870hznak">
            {primaryTitle}
          </label>
          <select id="scope-select" value={primaryId} onChange={e => onSelectPrimary(e.target.value)} className="-ml-1 text-xl font-bold text-[#0F172A] font-heading bg-transparent rounded-md px-1 py-0.5 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/30 cursor-pointer" data-id="element-5763" data-mp="29vmj8l2j43ud">
            {primaryOptions.map(o => <option key={o.id} value={o.id} data-id="element-5764" data-mp="1q9q4cavstk1l">
                {o.label[lang]}
              </option>)}
          </select>
        </div>

        {showSecondary && <div className="flex flex-col gap-1.5 min-w-0 flex-1" data-id="element-5765" data-mp="31naqit6ga06c">
            <div className="text-xs text-slate-500 uppercase tracking-wide" data-id="element-5766" data-mp="31poksztb38cu">
              {secondaryTitle}
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-5767" data-mp="xk9463fcjcmw">
              {secondaryOptions.length > 1 && <button type="button" onClick={onSelectAllSecondary} aria-pressed={allSelected} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${allSelected ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-5768" data-mp="1bka85rqwr06l">
                  {t('Tous', 'All')}
                </button>}
              {secondaryOptions.map(o => {
            const on = activeSecondaryIds.includes(o.id);
            return <button key={o.id} type="button" onClick={() => onToggleSecondary(o.id)} aria-pressed={on} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`} data-id="element-5769" data-mp="2imlryfqzpg">
                    {o.label[lang]}
                  </button>;
          })}
            </div>
          </div>}
      </div>

      <div className="py-4 flex flex-col gap-3" data-id="element-5770" data-mp="2uhn9ymqcseyl">
        <div className="px-5 flex items-center justify-between gap-3" data-id="element-5771" data-mp="2gj6qrwbb0vzk">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide" data-id="element-5772" data-mp="2gl1c3bqhmq46">
            {t('Performance par service', 'Performance by service')}
          </div>
          <div className="text-xs text-slate-500" data-id="element-5773" data-mp="151xcdo718u54">
            {totalRequests.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')}{' '}
            {t('requêtes', 'requests')}
          </div>
        </div>
        <PerformanceTable rows={rows} data-id="element-5774" data-mp="21rh9o2ep69q0" />
      </div>

    </div>;
}
/** Readable period, e.g. "30 derniers jours" or "Du 12 août au 25 sept. 2026". */
function periodLabelFor(range: DateRange): Localized {
  if (range.preset !== 'custom') return PERIOD_LABELS[range.preset];
  const f = (iso: string, locale: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  return {
    fr: `Du ${f(range.from, 'fr-CA')} au ${f(range.to, 'fr-CA')}`,
    en: `${f(range.from, 'en-CA')} – ${f(range.to, 'en-CA')}`
  };
}
export function RequetesAnalytics({
  onOpenRequests
}: {
  onOpenRequests?: () => void;
} = {}) {
  const {
    t,
    lang
  } = useLanguage();
  const {
    city
  } = useCity();
  const {
    account
  } = useAccount();
  const cityDistricts = districtsFor(city);
  const hasDistricts = city === 'Brossard';
  const citySectors = sectorsFor(city);

  // Access scope from the signed-in account's job: a councillor only sees
  // their own district; the mayor and directors see the whole city.
  const lockedDistrict = hasDistricts && account.scope.kind === 'district' ? cityDistricts.find(d => d.id === `d${(account.scope as {
    district: number;
  }).district}`) ?? null : null;
  const districts = lockedDistrict ? [lockedDistrict] : cityDistricts;
  const allSectors = lockedDistrict ? lockedDistrict.sectors : citySectors;
  const scopeFactor = lockedDistrict ? lockedDistrict.sectors.reduce((sum, s) => sum + sectorShare(lockedDistrict, s.id), 0) / Math.max(1, citySectors.length) : 1;
  const scopeSeed = lockedDistrict ? hashOf(lockedDistrict.id) : 0;
  const [mode, setMode] = useState<'district' | 'sector'>('district');
  const [mapExpanded, setMapExpanded] = useState(false);
  const [showPins, setShowPins] = useState(false);
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [slaFilter, setSlaFilter] = useState<SlaState | null>(null);
  // Whole city by default; a district-limited account falls back to its district.
  const [districtId, setDistrictId] = useState<string>(ALL_SCOPE);
  const [sectorId, setSectorId] = useState<string>(ALL_SCOPE);
  const [secondaryIds, setSecondaryIds] = useState<string[]>([]);
  const isSectorMode = hasDistricts && mode === 'sector';
  // "Toute la ville" is a selectable scope, except for a district-limited account.
  const canSelectAll = !lockedDistrict;
  const isAll = canSelectAll && (isSectorMode ? sectorId === ALL_SCOPE : districtId === ALL_SCOPE);
  const district = isAll && !isSectorMode ? undefined : districts.find(d => d.id === districtId) ?? districts[0];
  const sector = isAll && isSectorMode ? undefined : allSectors.find(s => s.id === sectorId) ?? allSectors[0];
  const allOption: ScopeOption[] = canSelectAll ? [{
    id: ALL_SCOPE,
    label: {
      fr: 'Toute la ville',
      en: 'Whole city'
    }
  }] : [];
  const primaryOptions: ScopeOption[] = [...allOption, ...(isSectorMode ? allSectors.map(s => ({
    id: s.id,
    label: s.label
  })) : districts.map(d => ({
    id: d.id,
    label: d.label
  })))];
  const secondaryOptions: ScopeOption[] = isSectorMode || isAll ? [] : (district?.sectors ?? []).map(s => ({
    id: s.id,
    label: s.label
  }));
  const kept = secondaryIds.filter(id => secondaryOptions.some(o => o.id === id));
  const activeSecondaryIds = kept.length > 0 ? kept : secondaryOptions.map(o => o.id);
  const units: ScopeUnit[] = isAll ? allSectors.map(s => ({
    key: s.id,
    letter: letterOf(s.label.fr),
    share: 1,
    baseSectorId: s.id
  })) : isSectorMode ? sector ? [{
    key: sector.id,
    letter: letterOf(sector.label.fr),
    share: lockedDistrict ? sectorShare(lockedDistrict, sector.id) : 1,
    baseSectorId: sector.id
  }] : [] : activeSecondaryIds.map(sid => {
    const s = allSectors.find(x => x.id === sid);
    return {
      key: sid,
      letter: s ? letterOf(s.label.fr) : '',
      // A sector split between districts only brings its own part.
      share: district ? sectorShare(district, sid) : 1,
      baseSectorId: sid
    };
  });
  const selectPrimary = (id: string) => {
    if (isSectorMode) setSectorId(id);else setDistrictId(id);
    setSecondaryIds([]);
  };
  const toggleSecondary = (id: string) => {
    const next = activeSecondaryIds.includes(id) ? activeSecondaryIds.filter(x => x !== id) : [...activeSecondaryIds, id];
    if (next.length > 0) setSecondaryIds(next);
  };
  /** Switch the map view, carrying the current selection over. */
  const changeView = (next: 'district' | 'sector') => {
    if (next === mode) return;
    if (isAll) {
      // Whole city stays selected in the other view.
      if (next === 'sector') setSectorId(ALL_SCOPE);else setDistrictId(ALL_SCOPE);
    } else if (next === 'sector') {
      const nextSector = activeSecondaryIds[0] ?? district?.sectors[0]?.id;
      if (nextSector) setSectorId(nextSector);
    } else if (sector) {
      // The district holding the largest part of the sector.
      const owner = districts.filter(d => d.sectors.some(s => s.id === sector.id)).sort((a, b) => sectorShare(b, sector.id) - sectorShare(a, sector.id))[0];
      if (owner) setDistrictId(owner.id);
    }
    setSecondaryIds([]);
    setMode(next);
  };
  const highlightedDistrictIds = isSectorMode ? [] : district ? [district.id] : [];
  /** Select the whole city (home button on the map, or the panel's drop-down). */
  const selectAll = () => {
    if (!canSelectAll) return;
    if (isSectorMode) setSectorId(ALL_SCOPE);else setDistrictId(ALL_SCOPE);
    setSecondaryIds([]);
  };
  const pinSectorKey = units.map(u => u.baseSectorId).join(',');
  const pins = useMemo(() => {
    if (!showPins) return [];
    // Sample data has no dates: keep a share of requests proportional to the period.
    const share = Math.min(1, 0.25 + rangeDays(dateRange) / 40);
    return requestPinsFor(city, pinSectorKey.split(',')).filter(
    // The few late / at-risk requests are still open, so they show on every period.
    p => p.state !== 'ok' || p.number % 100 / 100 < share);
  }, [showPins, city, pinSectorKey, dateRange]);
  // Switching account resets the selection to what the new person may see.
  useEffect(() => {
    setSecondaryIds([]);
    setSelectedPinId(null);
    setSlaFilter(null);
  }, [account.id]);

  // Detail closes on its own when the pin leaves the map (details off, other scope).
  const selectedPin = pins.find(p => p.id === selectedPinId && (slaFilter === null || p.state === slaFilter)) ?? null;

  // Large map: the detail sits below it, so jump to it like an anchor.
  const detailRef = useRef<HTMLDivElement>(null);
  const selectedPinKey = selectedPin?.id ?? null;
  const expandedRef = useRef(mapExpanded);
  expandedRef.current = mapExpanded;
  useEffect(() => {
    if (!expandedRef.current || !selectedPinKey) return;
    const id = window.setTimeout(() => {
      detailRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 50);
    return () => window.clearTimeout(id);
  }, [selectedPinKey]);
  // One set of demo figures feeds the KPIs, ATNA, the situations and the report.
  const lockedId = lockedDistrict?.id ?? null;
  const periodLabel = periodLabelFor(dateRange);
  const stats = statsFor(lockedId, rangeDays(dateRange));
  const briefing = buildBriefing(stats, lockedId);
  const attention = [...briefing.issues, briefing.result];
  const [reportOpen, setReportOpen] = useState(false);
  const closeReport = useCallback(() => setReportOpen(false), []);
  const [atnaQuestion, setAtnaQuestion] = useState<{
    id: number;
    text: string;
  } | null>(null);
  const atnaRef = useRef<HTMLDivElement>(null);
  const askAtna = (q: Localized) => {
    setAtnaQuestion({
      id: Date.now(),
      text: q[lang]
    });
    atnaRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const mapSectionRef = useRef<HTMLElement>(null);
  const scrollToMap = () => mapSectionRef.current?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
  /** Turn on the pins, optionally filter by state, and scroll to the map. */
  const showOnMap = (state: SlaState | null) => {
    setShowPins(true);
    setSlaFilter(state);
    setSelectedPinId(null);
    if (state && hasDistricts && !lockedDistrict) {
      // District 3 holds 1 late + 2 at-risk requests; district 9 has 1 late + 3 at risk.
      setMode('district');
      setDistrictId('d3');
      setSecondaryIds([]);
    }
    scrollToMap();
  };
  const openDossiers = (target: AttentionTarget) => {
    if (target === 'map-late') showOnMap('late');else if (target === 'map-risk') showOnMap('risk');else if (target === 'map-open') showOnMap(null);else scrollToMap();
  };
  const onKpi = (target: KpiTarget) => {
    if (target === 'requests') {
      if (onOpenRequests) onOpenRequests();else askAtna(ATNA_DATA_SUGGESTIONS[1]);
    } else if (target === 'open') showOnMap(null);else if (target === 'late') showOnMap(stats.late > 0 ? 'late' : null);else if (target === 'sla') scrollToMap();else askAtna(ATNA_DATA_SUGGESTIONS[3]);
  };
  const scopeLabel: Localized = isSectorMode ? sector?.label ?? {
    fr: 'Toute la ville',
    en: 'Whole city'
  } : district?.label ?? {
    fr: 'Toute la ville',
    en: 'Whole city'
  };
  return <div className="space-y-10 animate-in fade-in duration-300" data-id="element-5775" data-mp="1aryo5kqbh9ew">
      <section className="flex flex-col gap-2" data-id="element-5776" data-mp="1aqdr9ogj5edv">
        <h2 className="text-xl font-bold text-[#0F172A] font-heading" data-id="element-5777" data-mp="16r4yaltbdkjd">
          {t('Suivi des demandes citoyennes', 'Citizen request tracking')}
        </h2>
      </section>

      <DashboardKpis stats={stats} dateRange={dateRange} onDateRangeChange={setDateRange} onSelect={onKpi} data-id="element-5778" data-mp="1yht2k6qab619" />

      <section ref={mapSectionRef} className="flex flex-col gap-4 scroll-mt-6" data-id="element-5779" data-mp="2i51qbq747rdb">
        <div className="flex flex-col gap-1" data-id="element-5780" data-mp="2mws1fydvmj5s">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5781" data-mp="2mymrts7x33h9">
            {lockedDistrict ? t(`Respect des SLA · ${lockedDistrict.label.fr}`, `SLA compliance · ${lockedDistrict.label.en}`) : t('Respect des SLA sur le territoire', 'SLA compliance across the territory')}
          </h2>
        </div>
        <div className={`grid grid-cols-1 gap-6 ${mapExpanded ? '' : 'lg:grid-cols-2'}`} data-id="element-5782" data-mp="35umjf1wzg50b">
          <DistrictMap expanded={mapExpanded} onToggleExpanded={() => setMapExpanded(v => !v)} showDetails={showPins} onToggleDetails={() => setShowPins(v => !v)} pins={pins} selectedPinId={selectedPin?.id ?? null} onPinClick={id => setSelectedPinId(cur => cur === id ? null : id)} dateRange={dateRange} onDateRangeChange={setDateRange} slaFilter={slaFilter} onSlaFilterChange={setSlaFilter} key={`map-${account.id}`} homePoints={lockedDistrict ? districtPoints(lockedDistrict) : undefined} homeLabel={lockedDistrict ? t(`Recentrer sur le ${lockedDistrict.label.fr}`, `Recenter on ${lockedDistrict.label.en}`) : undefined} districts={districts} watchDistrictIds={Object.keys(WATCH_DISTRICTS)} watchSectorIds={WATCH_SECTOR_IDS} highlightedDistrictIds={highlightedDistrictIds} overlaySectors={isSectorMode ? allSectors : district?.polygon ? district.sectors : []} activeSectorIds={isSectorMode ? sector ? [sector.id] : [] : activeSecondaryIds} focusPoints={isAll ? allSectors.flatMap(s => s.polygon) : isSectorMode ? sector?.polygon ?? [] : district ? districtPoints(district) : []} onHome={canSelectAll ? selectAll : undefined} onDistrictClick={selectPrimary} onSectorClick={isSectorMode ? selectPrimary : undefined} view={isSectorMode ? 'sector' : 'district'} onViewChange={hasDistricts ? changeView : undefined} data-id="element-5783" data-mp="2r081l4p5n1n8" />
          {selectedPin ? <div ref={detailRef} className="scroll-mt-6" data-id="element-5784" data-mp="2siz79ds5mc7y">
              <RequestDetailPanel pin={selectedPin} onClose={() => setSelectedPinId(null)} data-id="element-5785" data-mp="2nrqxggmwwou9" />
            </div> : units.length > 0 && <DistrictDetailPanel mode={isSectorMode ? 'sector' : 'district'} showSecondary={hasDistricts && !isSectorMode && !isAll} primaryOptions={primaryOptions} primaryId={isAll ? ALL_SCOPE : isSectorMode ? sector?.id ?? '' : district?.id ?? ''} onSelectPrimary={selectPrimary} secondaryOptions={secondaryOptions} activeSecondaryIds={activeSecondaryIds} onToggleSecondary={toggleSecondary} onSelectAllSecondary={() => setSecondaryIds([])} units={units} scopeLabel={scopeLabel} data-id="element-5786" data-mp="200bqgak6xjco" />}
        </div>
      </section>

      <TerritorialBriefingPanel key={`briefing-${account.id}`} briefing={briefing} scopeLabel={lockedDistrict ? lockedDistrict.label : null} onOpen={openDossiers} aside={/* Height follows the result card (min. 380 px); the conversation scrolls inside. */
    <div ref={atnaRef} className="relative lg:min-h-[380px] scroll-mt-6" data-id="element-5788" data-mp="34z8eiuncw7d1">
            <AtnaDataAssistant scope={{
        label: lockedDistrict ? lockedDistrict.label : null,
        districtId: lockedId,
        period: periodLabel,
        stats
      }} externalQuestion={atnaQuestion} data-id="element-5789" data-mp="393g5yzl781ey" />
          </div>} data-id="element-5787" data-mp="y90upeqjkv25" />

      <div className="flex justify-center pt-2 pb-4 border-t border-slate-200" data-id="element-5790" data-mp="15s1lczg8uu83">
        <button onClick={() => setReportOpen(true)} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F172A] hover:bg-slate-800 text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-5791" data-mp="10u8jujpvyoe5">
          <FileText className="w-4 h-4" data-id="element-5792" data-mp="mv0j3me2r87u" />
          {t('Générer le rapport pour le conseil municipal', 'Generate the city council report')}
        </button>
      </div>

      <CouncilReportDialog open={reportOpen} onClose={closeReport} scopeLabel={lockedDistrict ? lockedDistrict.label : null} periodLabel={periodLabel} priorities={attention} kpis={[{
      label: {
        fr: 'Demandes reçues',
        en: 'Requests received'
      },
      value: fmtNum(stats.received, lang)
    }, {
      label: {
        fr: 'Résolues dans les délais',
        en: 'Resolved on time'
      },
      value: `${stats.onTime}${lang === 'fr' ? ' %' : '%'}`
    }, {
      label: {
        fr: 'Délai médian de résolution',
        en: 'Median resolution time'
      },
      value: `${fmtDec(stats.medianDays, lang)} ${lang === 'fr' ? 'j' : 'd'}`
    }, {
      label: {
        fr: 'Satisfaction après traitement',
        en: 'Satisfaction after handling'
      },
      value: `${fmtDec(stats.satisfaction, lang)} / 5`
    }, {
      label: {
        fr: 'Demandes en cours (à ce jour)',
        en: 'Open requests (today)'
      },
      value: fmtNum(stats.open, lang)
    }, {
      label: {
        fr: 'Demandes en retard (à ce jour)',
        en: 'Late requests (today)'
      },
      value: fmtNum(stats.late, lang)
    }]} data-id="element-5793" data-mp="hxqidal8ja4p" />
    </div>;
}